import { JaneGoldUserCallout } from '@iheartjane/menu';
import type { MarginProperties } from '@iheartjane/reefer';
import { useState } from 'react';

import { BloomBankLinkingModal } from '@jane/bloom/components/BloomBankLinkingModal/BloomBankLinkingModal';
import { ClientOnly } from '@jane/bloom/components/clientOnly';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { useAuthModal } from '@jane/bloom/providers/authModalProvider';

export interface BloomGoldCalloutBannerProps extends MarginProperties {
  /** Whether the signed-in user already has a linked bank account. */
  hasLinkedBank: boolean;
  /** Swaps the logged-out CTA copy to "Sign in for eligibility". */
  isSegmentedDeal?: boolean;
  isSignedIn: boolean;
  /** Pinned store state — drives the Ohio-specific Gold education copy. */
  storeState?: string | null;
}

/**
 * Jane Gold callout used on the Gold index and detail pages. Renders the shared
 * `JaneGoldUserCallout` and coordinates logging in (`useAuthModal`) → opening the
 * `BloomBankLinkingModal`.
 *
 * With the flag off it preserves the existing behavior: a callout whose CTA simply
 * links to `/login`.
 */
export const BloomGoldCalloutBanner = ({
  hasLinkedBank,
  isSegmentedDeal,
  isSignedIn,
  storeState,
  ...marginProps
}: BloomGoldCalloutBannerProps) => {
  const { appConfig } = useRootLoaderData();
  const { openAuthModal } = useAuthModal();
  const [showBankLinking, setShowBankLinking] = useState(false);
  const openBankLinking = () => setShowBankLinking(true);

  const handleLinkAccountClick = () => {
    if (isSignedIn) {
      openBankLinking();
    } else {
      openAuthModal({ onSuccess: openBankLinking });
    }
  };

  return (
    <>
      <JaneGoldUserCallout
        isSegmentedDeal={isSegmentedDeal}
        onLinkAccountClick={handleLinkAccountClick}
        storeState={storeState}
        userHasLinkedBank={hasLinkedBank}
        userSignedIn={isSignedIn}
        {...marginProps}
      />
      {/* ClientOnly: the modal touches `window` (useVisualViewportHeight) and
          would crash Bloom's SSR. Only mounted once opened. */}
      {showBankLinking && (
        <ClientOnly>
          {() => (
            <BloomBankLinkingModal
              aeropayConsumerId={appConfig.aeropay?.consumerId ?? ''}
              aeropayEnv={appConfig.aeropay?.env ?? ''}
              onRequestClose={() => setShowBankLinking(false)}
              open
              source="jane gold"
            />
          )}
        </ClientOnly>
      )}
    </>
  );
};
